/* ============================================================
   formRecovery.js — bring the user back to a form whose write failed.

   A form ARMS a draft (its key + field values) right before it submits. The
   next write apiClient sends CLAIMS it, so a draft can only ever belong to the
   request it was armed for. When that write fails on the network or with a 5xx,
   apiClient keeps the claimed draft as the latest failure and its alert offers
   "Reopen form". Clicking it flags a restore: FormRecoveryNavigator (inside the
   router) goes back to the route the form was on, and the form's owner, via
   useFormRecovery, reopens it with the values filled in. The user re-submits;
   nothing is re-sent automatically.

   Deliberately memory-only: drafts hold candidate details and CV files, so they
   never touch storage and a page reload drops them. Only the latest failure is
   kept.
   ============================================================ */

let armed = null
let failed = null
let restoring = false
let version = 0
const listeners = new Set()

function emit() {
  version += 1
  listeners.forEach((fn) => fn())
}

export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function getVersion() {
  return version
}

/**
 * Call synchronously right before the form's write is sent. `context` is what
 * the containers around the form need to reopen it (which candidate's modal,
 * which tab) — see useRecoveryHost.
 */
export function armDraft(key, values, context = null) {
  const { pathname, search } = window.location
  armed = { key, values, context, route: `${pathname}${search}` }
}

/** apiClient: take the draft armed for the write it is about to send. */
export function claimArmedDraft() {
  const draft = armed
  armed = null
  return draft
}

/** apiClient: the claimed draft's write failed; keep it for "Reopen form". */
export function recordFailedDraft(draft) {
  failed = draft
  restoring = false
  emit()
}

/** The alert's action. */
export function requestRestore() {
  if (!failed) return
  restoring = true
  emit()
}

/** The draft waiting to be restored, or null. */
export function pendingRestore() {
  return restoring ? failed : null
}

/** Called by the owner that reopened the form. */
export function finishRestore() {
  failed = null
  restoring = false
  emit()
}
